import ContentTypeBadge from 'components/badge/ContentTypeBadge';
import QuizStatusBadge from 'components/badge/QuizStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';

import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import { NavLink, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { DivConst } from 'constants/divConst';
import WsRespondentSearchEmpty from './components/WsRespondentSearchEmpty';
import { useWsRespondentSearch } from './hooks/useWsRespondentSearch';
import {
  formatNumberToCurrency,
  formatRateToPercent,
  formatTimeToDate,
  getChannelAcquiredCount,
  getChannelAcquisitionRate,
  getContentImageUrl,
  isEmpty,
  isNotEmpty,
} from 'utils/app';
import { getContentsTypeStr } from 'utils/content';

import type { JSX } from 'react';

/**
 * 獲得リスト画面
 */
export default function WsRespondentSearch(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { pageNo } = useParams();

  const [acctState] = useAtom(AcctState);

  const {
    initialized,
    processing,
    pageNo: pageNoState,
    contents,
    hasNext,
    isExpiredPlan,
    modalCd,
    modalContentsType,
    modalOpen,
    setModalOpen,
    onModalReportDownloadOpen,
    onPrev,
    onNext,
    onClickDetail,
  } = useWsRespondentSearch(pageNo, acctState.company);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">獲得リスト</h2>
            </div>
          </div>
        </div>
      </section>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={processing} />
      {/* クイズ未登録の場合の表示*/}
      {initialized && pageNoState == 1 && isEmpty(contents) && <WsRespondentSearchEmpty />}
      {isNotEmpty(contents) && (
        <section className="section-respondent">
          <div className="container">
            <div className="box-common box-common-lg">
              <div className="table-responsive js-scrollable">
                <table className="table table-top-gray table-top-gray-list">
                  <thead>
                    <tr>
                      <th className="col-1">コンテンツタイトル</th>
                      <th className="col-2">
                        日付
                        <span>▲</span>
                      </th>
                      <th className="col-3">参加数</th>
                      <th className="col-4">リスト数</th>
                      <th className="col-5">獲得率</th>
                      <th className="col-3">完了数</th>
                      <th className="col-5">完了率</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {contents.map(content => {
                      return (
                        <tr key={`ws-respondent-search-${content.cd}`}>
                          <th>
                            <div className="d-flex d-flex-table">
                              <div className="flex-shrink-0 flex-img">
                                {/* コンテンツ画像の表示 */}
                                <img
                                  src={getContentImageUrl(content.imageKey)}
                                  className="img-fluid"
                                />
                              </div>
                              {/* コンテンツタイトルの表示 */}
                              <div className="flex-grow-1 flex-text justify-content-start">
                                {isExpiredPlan ? (
                                  // プラン（利用期間切れ）の場合はリンクにしない
                                  <div className="detail mb-1">{content.title}</div>
                                ) : (
                                  <NavLink
                                    to={
                                      content.contentsTypeDiv ===
                                      DivConst.items.contentsType.voiceChat.value
                                        ? `/ws/${acctState.company.cd}/report/voice_chat/${content.cd}`
                                        : `/ws/${acctState.company.cd}/respondent/${getContentsTypeStr(content.contentsTypeDiv)}/${content.cd}`
                                    }
                                    className="detail hover quiz-title mb-1"
                                  >
                                    {content.title}
                                  </NavLink>
                                )}
                                <span>
                                  <ContentTypeBadge contentsTypeDiv={content.contentsTypeDiv} />
                                </span>
                              </div>
                            </div>
                          </th>
                          <td>
                            {/* 登録日時の表示 */}
                            <p className="date-time">
                              {formatTimeToDate(content.publishedAt, 'yyyy年M月dd日 h:mm a')}
                            </p>
                            {/* クイズステータスの表示 */}
                            <QuizStatusBadge quizStatusDiv={content.statusDiv} />
                          </td>
                          <td>
                            {/* 参加数 */}
                            {formatNumberToCurrency(content.participatedCount)}
                            <p className="number-container">
                              {/* <span className="icon-ani">
                                    <img src="/img/icon/arrow-up.svg" alt="" />
                                  </span>
                                  <span className="text-blue number">1.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* リスト数 */}
                            {formatNumberToCurrency(
                              getChannelAcquiredCount(
                                content.lineFlag,
                                content.mailAddressCount,
                                content.lineCount
                              )
                            )}
                            <p className="number-container">
                              {/* <span className="icon-ani">
                                    <img
                                      src="/img/icon/arrow-down.svg"
                                      alt=""
                                    />
                                  </span>
                                  <span className="text-pink number">4.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* 獲得率 */}
                            {`${formatRateToPercent(
                              getChannelAcquisitionRate(
                                content.lineFlag,
                                content.mailAddressAcquisitionRate,
                                content.lineAddressAcquisitionRate
                              )
                            )}%`}

                            <p className="Address-container">
                              {/* <span className="icon-ani">
                                    <img src="/img/icon/arrow-up.svg" alt="" />
                                  </span>
                                  <span className="text-blue number">1.3%</span> */}
                            </p>
                          </td>
                          <td>
                            {/* 完了数 */}
                            {formatNumberToCurrency(content.answeredCount)}
                            <p className="number-container"></p>
                          </td>
                          <td>
                            {/* 完了率 */}
                            {`${formatRateToPercent(content.answeredRate)}%`}

                            <p className="number-container">
                              {/* <span className="icon-ani">
                                        <img
                                          src="/img/icon/arrow-up.svg"
                                          alt=""
                                        />
                                      </span>
                                      <span className="text-blue number">
                                        1.3%
                                      </span> */}
                            </p>
                          </td>
                          <td>
                            <div className="d-flex">
                              <div className="flex-grow-1 item-button">
                                <a
                                  onClick={() => onClickDetail(content)}
                                  className={
                                    isExpiredPlan
                                      ? 'btn btn-disabled btn-common-w110 outline-blue'
                                      : 'btn btn-common btn-common-w110 moderate-blue'
                                  }
                                  style={{ whiteSpace: 'nowrap' }}
                                  data-tooltip-id="custom-tooltip"
                                  data-tooltip-content="プランの期限がきれました。プランのアップグレードをご検討ください。"
                                >
                                  リスト詳細
                                </a>
                                <a
                                  onClick={() => onModalReportDownloadOpen(content)}
                                  className={
                                    isExpiredPlan
                                      ? 'btn btn-disabled btn-common-w110 outline-blue'
                                      : 'btn btn-common btn-common-w110 outline-blue'
                                  }
                                  style={{ whiteSpace: 'nowrap' }}
                                  data-tooltip-id="custom-tooltip"
                                  data-tooltip-content="プランの期限がきれました。プランのアップグレードをご検討ください。"
                                >
                                  リスト出力
                                </a>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ページングボタン */}
              <nav
                aria-label="Page navigation"
                className="navigation mt-4"
                style={{ position: 'static' }}
              >
                <ul className="pagination mb-0">
                  <li className="page-item">
                    <a
                      onClick={onPrev}
                      className={`page-link prev ${pageNoState <= 1 && `disabled`}`}
                      aria-label="Previous"
                    >
                      <span aria-hidden="true"></span>
                    </a>
                  </li>
                  <li className="page-item">
                    <a
                      onClick={onNext}
                      className={`page-link next ${!hasNext && `disabled`}`}
                      aria-label="Next"
                    >
                      <span aria-hidden="true"></span>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </section>
      )}
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalOpen}
        setOpen={setModalOpen}
        cd={modalCd}
        contentsType={modalContentsType}
        reportDateFrom={null}
        reportDateTo={null}
      />
      {
        // プラン（利用期間切れ）の場合に表示する
        isExpiredPlan && <Tooltip id="custom-tooltip" place="top" variant="info" />
      }
    </>
  );
}
